
// src/components/features/data-banner/DataBanner.jsx
import { useState, useEffect } from 'react';
import { Text, Flex, Grid, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, useColorMode, useBreakpointValue, Button, keyframes, Input, InputGroup, InputLeftAddon, ButtonGroup, VStack } from '@chakra-ui/react';
import { SingleDataValue } from '../../data-display';
import { BitcoinPrice } from './BitcoinPrice';
import { ThemedLogoIcon } from '../../../theme/ThemedLogoIcon';
import { faBtc } from "@fortawesome/free-brands-svg-icons";
import { CustomSpinner } from '../../data-display/CustomSpinner';
import { daysSince, daysLeft } from '../../../utils';
import { currencySymbols } from '../../../utils/currencyConversion.utils';
import { createCurrencyConverter } from '../../../utils/currencyConversion.utils';
import { useRecoilState } from 'recoil';
import { selectedCurrencyState } from '../../../recoilState';
import { MetricsTutorial } from './MetricsTutorial';

// Define the wiggle animation
const wiggle = keyframes`
  0% { transform: rotate(0deg); }
  5% { transform: rotate(-5deg); }
  10% { transform: rotate(5deg); }
  15% { transform: rotate(-5deg); }
  20% { transform: rotate(5deg); }
  25% { transform: rotate(-4deg); }
  30% { transform: rotate(4deg); }
  35% { transform: rotate(-4deg); }
  40% { transform: rotate(4deg); }
  45% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
  55% { transform: rotate(-3deg); }
  60% { transform: rotate(3deg); }
  65% { transform: rotate(-2deg); }
  70% { transform: rotate(2deg); }
  75% { transform: rotate(-2deg); }
  80% { transform: rotate(2deg); }
  85% { transform: rotate(-1deg); }
  90% { transform: rotate(1deg); }
  95% { transform: rotate(-1deg); }
  100% { transform: rotate(0deg); }
`;

export const DataBanner = ({
  formatNumber,
  formatLargeNumber,
  lastMempoolBlockData,
  fees,
  mempoolInfo,
  data,
  bitcoinPrice,
  da,
  blocks
}) => {

  const dataOptions = [
    // Default metrics (DO NOT CHANGE ORDER - first 7)
    { id: 'blockHeight', label: 'Block Height' },
    { id: 'hashRate7d', label: 'Hash Rate 7d' },
    { id: 'unconfirmedTxs', label: 'Unconfirmed' },
    { id: 'highFeePerVbyte', label: 'Fastest Fee' },
    { id: 'miningRevenue24h', label: 'Mining Rev 24h' },
    { id: 'satsPerDollar', label: 'Sats~Fiat' },
        { id: 'avgBlockTime', label: 'Avg Block Time' },


    // Core Network Status
    { id: 'customBag', label: 'Stack Track' },
    { id: 'currentDifficulty', label: 'Difficulty' },
    { id: 'mempoolTotalSize', label: 'Mempool Size' },
    { id: 'mempoolValue', label: 'Mempool Value' },
    { id: 'mempoolTotalFees', label: 'Mempool Fees' },

    // Fee Market
    { id: 'economyFee', label: 'Economy Fee' },
    { id: 'halfHourFee', label: '30m Fee' },
    { id: 'hourFee', label: 'Hour Fee' },
    { id: 'lastBlockAvgFee', label: 'Last Blk Avg Fee' },
    { id: 'medianBlockFee', label: 'Median Blk Fee' },
    { id: 'medianFee10Blocks', label: 'Median Fee 10B' },

    // Price & Market
    { id: 'bitcoinPrice', label: 'Bitcoin Price' },
    { id: 'marketCap', label: 'Market Cap' },
    { id: 'btcVol24H', label: 'Vol 24H' },
    { id: 'changePercent24H', label: '% Chng 24H' },
    { id: 'change24H', label: '$ Chng 24H' },
    { id: 'low24H', label: 'Low 24H' },
    { id: 'high24H', label: 'High 24H' },
    { id: 'change200d', label: '% Chng 200d' },
    { id: 'change1y', label: '% Chng 1Y' },
    { id: 'changeAth', label: '% from ATH' },
    { id: 'marketCapChangePercent', label: 'MCap Chng %' },
    { id: 'sixOneFive', label: '6.15 BTC Value' },

    // Supply & Halving
    { id: 'circulatingSupply', label: 'Circ Supply' },
    { id: 'percentageIssued', label: '% Issued' },
    { id: 'blocksToHalving', label: 'Blks to Halving' },
    { id: 'daysToHalving', label: 'Next Halving' },
    { id: 'assetAge', label: 'BTC Age (D)' },

    // Mining Economics
    { id: 'blockReward', label: 'Last Reward' },
    { id: 'lastBlockFees', label: 'Last Blk Fees' },
    { id: 'totalFees10Blocks', label: 'Total Fees 10B' },
    { id: 'feesToSubsidyPercent', label: 'Fees/Reward %' },
    { id: 'lastBlockPool', label: 'Last Blk Miner' },

    // Difficulty Adjustment
    { id: 'blocksToDiffAdj', label: 'Blks to Diff Adj' },
    { id: 'timeToNextDiff', label: 'Time to Diff' },
    { id: 'estimatedRetargetDate', label: 'Next Diff Date' },
    { id: 'diffProgressPercent', label: 'Diff Progress' },
    { id: 'diffChangePercent', label: 'Diff Change %' },
    { id: 'nextDifficultyEstimate', label: 'Next Diff Est' },
    { id: 'predictedNextDiff', label: 'Next Diff' },
    { id: 'previousRetarget', label: 'Prev Diff Δ' },

    // Block Statistics
    { id: 'lastBlockTxCount', label: 'Last Blk TXs' },
    { id: 'lastBlockSize', label: 'Last Blk Size' },
    { id: 'lastBlockWeight', label: 'Last Blk Weight' },
    { id: 'blockFullness', label: 'Block Fullness' },
    { id: 'availableBlockSpace', label: 'Available Space' },
    { id: 'newBlocks24h', label: 'New Blocks 24h' },
    { id: 'avgBlockSize10', label: 'Avg Size 10B' },
    { id: 'avgTxCount10', label: 'Avg TX 10B' },
    { id: 'emptyBlocks10', label: 'Empty Blks 10B' },
    { id: 'largestBlock10', label: 'Largest 10B' },

    // Transaction & UTXO Metrics
    { id: 'transactions24h', label: '# of TX 24h' },
    { id: 'avgTxValue', label: 'Avg TX Value' },
    { id: 'largestTxValue24h', label: 'Largest TX 24h' },
    { id: 'txAllTime', label: 'Total TXs' },
    { id: 'coinDaysDestroyed24h', label: 'CDD 24h' },
    { id: 'lastBlockUtxoChange', label: 'Last UTXO Δ' },
    { id: 'avgUtxoPerBlock', label: 'UTXO Δ/Blk 10B' },

    // Lightning Network
    { id: 'lightningNodes', label: 'LN Nodes' },
    { id: 'lightningChannels', label: 'LN Channels' },
    { id: 'lightningCapacity', label: 'LN Capacity' },
    { id: 'lightningAvgChannelSize', label: 'LN Avg Channel' },

    // Purchasing Power (One BTC Can Buy)
    { id: 'goldToBtc', label: 'Gold~BTC' },
    { id: 'silverToBtc', label: 'Silver~BTC' },
    { id: 'oilBarrelsToBtc', label: 'Barrels~BTC' },
    { id: 'bigMacToBtc', label: 'Big Macs~BTC' },
    { id: 'coffeesToBtc', label: 'Coffees~BTC' },
    { id: 'housesToBtc', label: 'Houses~BTC' },
    { id: 'eggsToBtc', label: 'Eggs~BTC' },
    { id: 'naturalGasToBtc', label: 'NatGas MCF~BTC' },
    { id: 'iPhonesToBtc', label: 'iPhones~BTC' },
  ];

  const [conversionRates, setConversionRates] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useRecoilState(selectedCurrencyState);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedData, setSelectedData] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [currentlySelectedId, setCurrentlySelectedId] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [animateMetrics, setAnimateMetrics] = useState(false);
  const [bitcoinPriceDisplayMode, setBitcoinPriceDisplayMode] = useState('price');
  const [vers, setVers] = useState('1');
  const [versType, setVersType] = useState('btc'); // 'btc' or 'sats'

  // Helper function to format numbers without trailing zeros
  const formatNumberClean = (value, decimals = 0) => {
    const formatted = formatNumber(value, decimals);
    // Remove trailing zeros after decimal point
    if (decimals > 0 && formatted.includes('.')) {
      return formatted.replace(/\.?0+$/, '');
    }
    return formatted;
  };


  useEffect(() => {
    if (data) {
      const newConversionRates = createCurrencyConverter(data);
      setConversionRates(newConversionRates);
    }
  }, [data]);

  const convertBitcoinPrice = (bitcoinPrice) => {
    if (selectedCurrency && conversionRates && conversionRates[selectedCurrency]) {
      return bitcoinPrice * conversionRates[selectedCurrency];
    }
    return bitcoinPrice;
  };

  const breakpoint = useBreakpointValue({ base: 'base', xs: 'xs', sm: 'sm', 'sm2': 'sm2', md: 'md', lg: 'lg', xl: 'xl', '2xl': '2xl' });

  const { colorMode } = useColorMode();
  const modalBgColor = colorMode === 'dark' ? '#121212' : 'white';
  const modalTextColor = colorMode === 'dark' ? 'white' : '#121212';
  const modalBorderColor = colorMode === 'dark' ? 'white' : '#121212';
  const itsUpToYou = colorMode === 'dark' ? '#666666' : '#4A5568';

  const [selectedPosition, setSelectedPosition] = useState(null);
  const [modalStep, setModalStep] = useState('select'); // 'select' or 'configure'

  const onOpen = (id, position) => {
    setCurrentlySelectedId(id);
    setSelectedPosition(position);
    setModalStep('select');
    setIsOpen(true);
  };
  const onClose = () => {
    setIsOpen(false);
    setCurrentlySelectedId(null);
    setSelectedPosition(null);
    setModalStep('select');
  };

  const handleTutorialClose = () => {
    setShowTutorial(false);
    setAnimateMetrics(true);

    // Remove animation after 1.5 seconds
    setTimeout(() => {
      setAnimateMetrics(false);
    }, 1500);
  };

  const handleSwap = (newId) => {
    // If selecting Stack Track, transition to configure step
    if (newId === 'customBag') {
      setModalStep('configure');
      return;
    }

    setSelectedData((prevData) => {
      const newData = [...prevData];
      const newIndex = dataOptions.findIndex((option) => option.id === newId);

      if (selectedPosition !== null && newIndex !== -1) {
        // Check if the new metric is already displayed
        const existingPosition = newData.findIndex((id) => id === newIndex);
        
        if (existingPosition !== -1) {
          // Swap the two metrics
          [newData[selectedPosition], newData[existingPosition]] = [newData[existingPosition], newData[selectedPosition]];
        } else {
          // Replace the metric at the selected position
          newData[selectedPosition] = newIndex;
        }
      }

      return newData;
    });

    onClose();
  };

  useEffect(() => {
    const storedCurrency = localStorage.getItem('selectedCurrency');
    if (storedCurrency) {
      setSelectedCurrency(storedCurrency);
    } else {
      setSelectedCurrency('usd');
    }
  }, [setSelectedCurrency]);

  useEffect(() => {
    if (selectedCurrency) {
      localStorage.setItem('selectedCurrency', selectedCurrency);
    }
  }, [selectedCurrency]);

  useEffect(() => {
    const storedData = localStorage.getItem('selectedData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      
      // Clean up removed metrics by finding their positions
      const cleanedData = parsedData.map(index => {
        // If the index is out of bounds, return a default
        if (index >= dataOptions.length) {
          return 0; // Default to first metric
        }
        return index;
      });
      
      setSelectedData(cleanedData);
    }
  }, [dataOptions.length]);

  useEffect(() => {
    localStorage.setItem('selectedData', JSON.stringify(selectedData));
  }, [selectedData]);

  useEffect(() => {
    // Load bitcoinPriceDisplayMode from localStorage
    const storedDisplayMode = localStorage.getItem('bitcoinPriceDisplayMode');
    if (storedDisplayMode) {
      setBitcoinPriceDisplayMode(storedDisplayMode);
    }
  }, []);

  useEffect(() => {
    // Save bitcoinPriceDisplayMode to localStorage
    localStorage.setItem('bitcoinPriceDisplayMode', bitcoinPriceDisplayMode);
  }, [bitcoinPriceDisplayMode]);

  useEffect(() => {
    // Load values from sessionStorage with obfuscated keys
    const storedVers = sessionStorage.getItem('vers');
    const storedVersType = sessionStorage.getItem('versMode');
    if (storedVers !== null) {
      setVers(storedVers);
    }
    if (storedVersType !== null) {
      setVersType(storedVersType);
    }
  }, []);

  useEffect(() => {
    // Save values to sessionStorage with obfuscated keys
    sessionStorage.setItem('vers', vers);
    sessionStorage.setItem('versMode', versType);
  }, [vers, versType]);

  useEffect(() => {
    // Check if tutorial has been dismissed before (using new key)
    const tutorialDismissed = localStorage.getItem('timechainStatsTutorialV2Dismissed');
    if (!tutorialDismissed) {
      setShowTutorial(true);
    }

    // Listen for custom event to show tutorial
    const handleShowTutorial = () => {
      setShowTutorial(true);
      // Scroll to top when showing tutorial
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    window.addEventListener('showMetricsTutorial', handleShowTutorial);

    return () => {
      window.removeEventListener('showMetricsTutorial', handleShowTutorial);
    };
  }, []);

  const valueMap = {
    bitcoinPrice: bitcoinPrice && selectedCurrency && currencySymbols[selectedCurrency] 
      ? `${currencySymbols[selectedCurrency]}${(() => {
          const converted = convertBitcoinPrice(bitcoinPrice);
          if (
            selectedCurrency.toLowerCase() === 'ars' ||
            selectedCurrency.toLowerCase() === 'jpy' ||
            selectedCurrency.toLowerCase() === 'krw' ||
            selectedCurrency.toLowerCase() === 'inr' ||
            selectedCurrency.toLowerCase() === 'php'
          ) {
            return formatNumber(converted, 0);
          } else {
            return formatNumber(converted, 2);
          }
        })()}`
      : 'Loading...',
    blockHeight: lastMempoolBlockData ? `${formatNumber(lastMempoolBlockData.height, 0)}` : 'Loading...',
    hashRate7d: data?.ts04?.hashrate?.currentHashrate != null ? `~${formatLargeNumber(data.ts04.hashrate.currentHashrate, 0, true)}H/s` : 'Loading...',
    unconfirmedTxs: mempoolInfo ?
      mempoolInfo.size < 10000
        ? `${formatNumber(mempoolInfo.size, 0)} TX`
        : `~${formatLargeNumber(mempoolInfo.size, 0, false)} TX`
      : 'Loading...',
    highFeePerVbyte: fees ? `${fees.fastestFee < 1 ? formatNumber(fees.fastestFee, 2) : formatNumber(fees.fastestFee, 0)} ṩ/vB` : 'Loading...',
    miningRevenue24h: data?.ts11?.rewards144?.totalReward != null && bitcoinPrice != null
      ? `~${selectedCurrency === 'usd' ? '$' : ''}${formatLargeNumber(
        (Number(data.ts11.rewards144.totalReward) / 100000000) * convertBitcoinPrice(bitcoinPrice),
        1,
        false
      )}`
      : 'Loading...',
    satsPerDollar: bitcoinPrice ? `${formatNumber((1 / convertBitcoinPrice(bitcoinPrice)) * 100000000, 0)}` : 'Loading...',
    blocksToHalving: lastMempoolBlockData ? `${formatNumber(1050000 - lastMempoolBlockData.height)}` : 'Loading...',
    daysToHalving: data?.s03?.halvening?.bitcoin_data?.halvening_time != null ? `~${formatNumber(daysLeft(data.s03.halvening.bitcoin_data.halvening_time), 0)} Days` : 'Loading...',
    blocksToDiffAdj: da != null ? `${da.remainingBlocks}` : 'Loading...',
    marketCap: data?.s06?.bitcoin?.market_data?.market_cap?.usd != null
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatLargeNumber(convertBitcoinPrice(data.s06.bitcoin.market_data.market_cap.usd), 2, false)}`
      : 'Loading...',

    btcVol24H: data?.s06?.bitcoin?.market_data?.total_volume?.usd != null
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatLargeNumber(convertBitcoinPrice(data.s06.bitcoin.market_data.total_volume.usd), 2, false)}`
      : 'Loading...',
    low24H: data?.s06?.bitcoin?.market_data?.low_24h?.[selectedCurrency] != null ? `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(data.s06.bitcoin.market_data.low_24h[selectedCurrency], 0)}` : 'Loading...',
    high24H: data?.s06?.bitcoin?.market_data?.high_24h?.[selectedCurrency] != null ? `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(data.s06.bitcoin.market_data.high_24h[selectedCurrency], 0)}` : 'Loading...',
    changePercent24H: data?.s06?.bitcoin?.market_data?.price_change_percentage_24h_in_currency?.[selectedCurrency] != null
      ? `${formatNumberClean(data.s06.bitcoin.market_data.price_change_percentage_24h_in_currency[selectedCurrency], 2)}%`
      : 'Loading...',
    change24H: data?.s06?.bitcoin?.market_data?.high_24h?.[selectedCurrency] && data?.s06?.bitcoin?.market_data?.low_24h?.[selectedCurrency]
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(
        data.s06.bitcoin.market_data.high_24h[selectedCurrency] -
        data.s06.bitcoin.market_data.low_24h[selectedCurrency],
        0
      )}`
      : 'Loading...',
    change200d: data?.s06?.bitcoin?.market_data?.price_change_percentage_200d_in_currency?.[selectedCurrency] != null
      ? `${formatNumberClean(data.s06.bitcoin.market_data.price_change_percentage_200d_in_currency[selectedCurrency], 2)}%`
      : 'Loading...',
    change1y: data?.s06?.bitcoin?.market_data?.price_change_percentage_1y_in_currency?.[selectedCurrency] != null
      ? `${formatNumberClean(data.s06.bitcoin.market_data.price_change_percentage_1y_in_currency[selectedCurrency], 2)}%`
      : 'Loading...',
    changeAth: data?.s06?.bitcoin?.market_data?.ath_change_percentage?.[selectedCurrency] != null
      ? `${formatNumberClean(data.s06.bitcoin.market_data.ath_change_percentage[selectedCurrency], 2)}%`
      : "Loading...",
    mempoolTotalSize: mempoolInfo ? `${formatLargeNumber(mempoolInfo.usage, 1, true)}B` : 'Loading...',
    // flowInExchange: data?.s12?.metrics?.data?.exchange_flows?.flow_in_exchange_native_units != null ? `${formatNumber(data.s12.metrics.data.exchange_flows.flow_in_exchange_native_units, 0)}` : 'Loading...',
    // flowOutExchange: data?.s12?.metrics?.data?.exchange_flows?.flow_out_exchange_native_units != null ? `${formatNumber(data.s12.metrics.data.exchange_flows.flow_out_exchange_native_units, 0)}` : 'Loading...',
    assetAge: `${formatNumber(daysSince("2009-01-03"))}`,
    lightningChannels: data?.s11?.lightning?.latest?.channel_count != null ? `${formatNumber(data.s11.lightning.latest.channel_count, 0)}` : 'Loading...',
    lightningCapacity: data?.s11?.lightning?.latest?.total_capacity != null ? `${formatNumber(data.s11.lightning.latest.total_capacity / 100000000, 0)} BTC` : 'Loading...',
    sixOneFive: bitcoinPrice ? `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(6.15 * convertBitcoinPrice(bitcoinPrice), 0)}` : 'Loading...',
    bigMacToBtc: bitcoinPrice ? `${formatNumber((bitcoinPrice / 5.15), 0)} 🍔` : "Loading...",
    silverToBtc: data?.s06?.bitcoin?.market_data?.current_price?.xag != null ? `${formatLargeNumber(data.s06.bitcoin.market_data.current_price.xag, 1, false)} oz. 🥈` : 'Loading...',
    goldToBtc: data?.s06?.bitcoin?.market_data?.current_price?.xau != null ? `${formatNumberClean(data.s06.bitcoin.market_data.current_price.xau, 1)} oz. 🥇` : 'Loading...',
    oilBarrelsToBtc: data?.s15?.oil_usd?.[0]?.spreadProfilePrices?.[0]?.ask != null && bitcoinPrice != null
      ? `${formatNumber(bitcoinPrice / data.s15.oil_usd[0].spreadProfilePrices[0].ask, 0)} 🛢️`
      : "Loading...",
    coffeesToBtc: bitcoinPrice ? `${formatNumber((bitcoinPrice / 4.50), 0)} ☕` : "Loading...",
    housesToBtc: bitcoinPrice ? `${formatNumberClean((bitcoinPrice / 400000), 2)} 🏠` : "Loading...",
    eggsToBtc: bitcoinPrice ? `${formatNumber((bitcoinPrice / 0.25), 0)} 🥚` : "Loading...",
    naturalGasToBtc: bitcoinPrice && data?.s15?.ngc_usd?.[0]?.spreadProfilePrices?.[0]?.ask
      ? `${formatNumberClean(bitcoinPrice / data.s15.ngc_usd[0].spreadProfilePrices[0].ask, 0)} 🔥`
      : "Loading...",
    iPhonesToBtc: bitcoinPrice ? `${formatNumber((bitcoinPrice / 999), 0)} 📱` : "Loading...",
    // avgUtxoAge: data?.s12?.metrics?.data?.on_chain_data?.average_utxo_age != null ? `${formatNumber(data.s12.metrics.data.on_chain_data.average_utxo_age / 365, 2)} Years` : 'Loading...',
    // In the valueMap object, replace this line:
    avgBlockTime: data?.s091?.btc_historical?.Data?.Data?.[1]?.block_time != null
      ? `${formatNumberClean(data.s091.btc_historical.Data.Data[1].block_time / 60, 1)} Mins`
      : 'Loading...',

    newBlocks24h: data?.s091?.btc_historical.Data?.Data &&
      data.s091.btc_historical.Data.Data[1] &&
      data.s091.btc_historical.Data.Data[0]
      ? `${formatNumber(data.s091.btc_historical.Data.Data[1].block_height - data.s091.btc_historical.Data.Data[0].block_height)}`
      : 'Loading...',
    // addressesOver1Btc: data?.s12?.metrics?.data?.on_chain_data?.addresses_balance_greater_1_native_units_count != null ? `${formatNumber(data.s12.metrics.data.on_chain_data.addresses_balance_greater_1_native_units_count, 0)}` : 'Loading...',
    transactions24h: data?.s08?.blockchain?.Data?.transaction_count != null
      ? `${formatNumber(data.s08.blockchain.Data.transaction_count, 0)}`
      : 'Loading...',
    avgTxValue: data?.s08?.blockchain?.Data?.average_transaction_value != null
      ? `${formatNumberClean(data.s08.blockchain.Data.average_transaction_value, 3)} BTC`
      : 'Loading...',
    txAllTime: data?.s091?.btc_historical.Data?.Data?.[0]?.transaction_count_all_time != null
      ? `${formatLargeNumber(data.s091.btc_historical.Data.Data[0].transaction_count_all_time, 2, false)}`
      : 'Loading...',
    coinDaysDestroyed24h: data?.s03?.bitcoin_stats?.bitcoin_data?.cdd_24h != null ? `${formatLargeNumber(data.s03.bitcoin_stats.bitcoin_data.cdd_24h, 1, true)}` : 'Loading...',
    predictedNextDiff: data?.s03?.bitcoin_stats?.bitcoin_data?.next_difficulty_estimate != null
      ? `${formatLargeNumber(data.s03.bitcoin_stats.bitcoin_data.next_difficulty_estimate, 2, false)}`
      : 'Loading...',

    nextDifficultyEstimate: data?.s03?.bitcoin_stats?.bitcoin_data?.next_difficulty_estimate != null &&
      data?.s03?.bitcoin_stats?.bitcoin_data?.difficulty != null
      ? `${((data.s03.bitcoin_stats.bitcoin_data.next_difficulty_estimate - data.s03.bitcoin_stats.bitcoin_data.difficulty) / data.s03.bitcoin_stats.bitcoin_data.difficulty * 100).toFixed(2)}%`
      : 'Loading...',
    circulatingSupply: data?.s12?.metrics?.data?.[0]?.marketData?.supply?.circulating != null ? `~${formatLargeNumber(data.s12.metrics.data[0].marketData.supply.circulating, 2, false)}` : 'Loading...',
    percentageIssued: data?.s12?.metrics?.data?.[0]?.marketData?.supply?.circulating != null ? `${formatNumberClean((data.s12.metrics.data[0].marketData.supply.circulating / 21000000) * 100, 2)}%` : 'Loading...',
    marketCapChangePercent: data?.s06?.bitcoin?.market_data?.market_cap_change_percentage_24h != null
      ? `${formatNumberClean(data.s06.bitcoin.market_data.market_cap_change_percentage_24h, 2)}%`
      : 'Loading...',
    // activeBtc24h: data?.s12?.metrics?.data?.supply_activity?.supply_active_1d != null ? `${formatNumber(data.s12.metrics.data.supply_activity.supply_active_1d, 0)}` : 'Loading...',
    // activeBtc30d: data?.s12?.metrics?.data?.supply_activity?.supply_active_30d != null ? `${formatNumber(data.s12.metrics.data.supply_activity.supply_active_30d, 0)}` : 'Loading...',
    // adjustedNVTRatio: data?.s12?.metrics?.data?.blockchain_stats_24_hours?.adjusted_nvt != null ? formatNumber(data.s12.metrics.data.blockchain_stats_24_hours.adjusted_nvt, 2) : 'Loading...',
    lightningNodes: data?.s11?.lightning?.latest?.node_count != null ? `${formatNumber(data.s11.lightning.latest.node_count, 0)}` : 'Loading...',
    lightningAvgChannelSize: data?.s11?.lightning?.latest?.total_capacity != null && data?.s11?.lightning?.latest?.channel_count != null
      ? `${formatNumberClean((data.s11.lightning.latest.total_capacity / data.s11.lightning.latest.channel_count) / 100000000, 6)}`
      : 'Loading...',
    // utxosInProfit: data?.s12?.metrics?.data?.on_chain_data?.utxo_in_profit_count != null ? `${formatLargeNumber(data.s12.metrics.data.on_chain_data.utxo_in_profit_count, 1, true)}` : 'Loading...',
    // utxosInLoss: data?.s12?.metrics?.data?.on_chain_data?.utxo_in_loss_count != null ? `${formatLargeNumber(data.s12.metrics.data.on_chain_data.utxo_in_loss_count, 1, true)}` : 'Loading...',
    // medianTxValue24h: data?.s12?.metrics?.data?.blockchain_stats_24_hours?.median_tx_value != null && bitcoinPrice != null
    //   ? `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(convertBitcoinPrice(data.s12.metrics.data.blockchain_stats_24_hours.median_tx_value), 2)}`
    //   : 'Loading...',
    largestTxValue24h: data?.s03?.bitcoin_stats?.bitcoin_data?.largest_transaction_24h?.value_usd != null && bitcoinPrice != null
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatLargeNumber(data.s03.bitcoin_stats.bitcoin_data.largest_transaction_24h.value_usd * conversionRates[selectedCurrency], 2, false)}`
      : 'Loading...',
    // New websocket-based metrics
    lastBlockTxCount: lastMempoolBlockData?.tx_count != null
      ? `${formatNumber(lastMempoolBlockData.tx_count, 0)} TX`
      : 'Loading...',
    lastBlockSize: lastMempoolBlockData?.size != null
      ? `${formatLargeNumber(lastMempoolBlockData.size, 2, true)}B`
      : 'Loading...',
    lastBlockFees: lastMempoolBlockData?.extras?.totalFees != null && bitcoinPrice != null
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(
        (lastMempoolBlockData.extras.totalFees / 100000000) * convertBitcoinPrice(bitcoinPrice),
        0
      )}`
      : 'Loading...',
    lastBlockAvgFee: lastMempoolBlockData?.extras?.avgFeeRate != null
      ? `${lastMempoolBlockData.extras.avgFeeRate < 1 ? formatNumber(lastMempoolBlockData.extras.avgFeeRate, 2) : formatNumber(lastMempoolBlockData.extras.avgFeeRate, 0)} ṩ/vB`
      : 'Loading...',
    lastBlockPool: lastMempoolBlockData?.extras?.pool?.name != null
      ? lastMempoolBlockData.extras.pool.name
      : 'Loading...',

    mempoolTotalFees: mempoolInfo?.total_fee != null
      ? (() => {
          const btcAmount = mempoolInfo.total_fee;
          if (btcAmount >= 1) {
            return `${formatNumberClean(btcAmount, 8)} BTC`;
          } else {
            const satsAmount = btcAmount * 100000000;
            return `${formatLargeNumber(satsAmount, 1, false)} sats`;
          }
        })()
      : 'Loading...',
    economyFee: fees?.economyFee != null
      ? `${fees.economyFee < 1 ? formatNumber(fees.economyFee, 2) : formatNumber(fees.economyFee, 0)} ṩ/vB`
      : 'Loading...',
    halfHourFee: fees?.halfHourFee != null
      ? `${fees.halfHourFee < 1 ? formatNumber(fees.halfHourFee, 2) : formatNumber(fees.halfHourFee, 0)} ṩ/vB`
      : 'Loading...',
    hourFee: fees?.hourFee != null
      ? `${fees.hourFee < 1 ? formatNumber(fees.hourFee, 2) : formatNumber(fees.hourFee, 0)} ṩ/vB`
      : 'Loading...',
    diffChangePercent: da?.difficultyChange != null
      ? `${formatNumberClean(da.difficultyChange, 2)}%`
      : 'Loading...',
    diffProgressPercent: da?.progressPercent != null
      ? `${formatNumberClean(da.progressPercent, 2)}%`
      : 'Loading...',
    timeToNextDiff: da?.remainingTime != null
      ? `~${formatNumberClean(da.remainingTime / (1000 * 60 * 60 * 24), 1)} D`
      : 'Loading...',
    currentDifficulty: lastMempoolBlockData?.difficulty != null
      ? `${formatLargeNumber(lastMempoolBlockData.difficulty, 2, false)}`
      : 'Loading...',
    blockReward: lastMempoolBlockData?.extras?.reward != null && bitcoinPrice != null
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(
        ((lastMempoolBlockData.extras.reward / 100000000) * convertBitcoinPrice(bitcoinPrice)),
        0
      )}`
      : 'Loading...',
    lastBlockWeight: lastMempoolBlockData?.weight != null
      ? `${formatLargeNumber(lastMempoolBlockData.weight, 2, true)} WU`
      : 'Loading...',
    lastBlockUtxoChange: lastMempoolBlockData?.extras?.utxoSetChange != null
      ? `${lastMempoolBlockData.extras.utxoSetChange > 0 ? '+' : ''}${formatNumber(lastMempoolBlockData.extras.utxoSetChange, 0)}`
      : 'Loading...',
    blockFullness: lastMempoolBlockData?.weight != null
      ? `${formatNumberClean((lastMempoolBlockData.weight / 4000000) * 100, 1)}%`
      : 'Loading...',
    availableBlockSpace: lastMempoolBlockData?.weight != null
      ? `${formatLargeNumber(4000000 - lastMempoolBlockData.weight, 2, true)} WU`
      : 'Loading...',
    feesToSubsidyPercent: lastMempoolBlockData?.extras?.totalFees != null && lastMempoolBlockData?.extras?.reward != null
      ? `${formatNumberClean((lastMempoolBlockData.extras.totalFees / lastMempoolBlockData.extras.reward) * 100, 2)}%`
      : 'Loading...',
    // New metrics from blocks array
    medianFee10Blocks: (() => {
      if (!blocks || blocks.length === 0) return 'Loading...';
      const last10Blocks = blocks.slice(0, Math.min(10, blocks.length));
      const fees = last10Blocks
        .map(block => block.extras?.avgFeeRate)
        .filter(fee => fee != null)
        .sort((a, b) => a - b);
      if (fees.length === 0) return 'Loading...';
      const median = fees.length % 2 === 0
        ? (fees[fees.length / 2 - 1] + fees[fees.length / 2]) / 2
        : fees[Math.floor(fees.length / 2)];
      return `${formatNumber(median, 0)} ṩ/vB`;
    })(),
    avgBlockSize10: (() => {
      if (!blocks || blocks.length === 0) return 'Loading...';
      const last10Blocks = blocks.slice(0, Math.min(10, blocks.length));
      const avgSize = last10Blocks.reduce((sum, block) => sum + (block.size || 0), 0) / last10Blocks.length;
      return `${formatLargeNumber(avgSize, 2, true)}B`;
    })(),
    totalFees10Blocks: (() => {
      if (!blocks || blocks.length === 0 || !bitcoinPrice) return 'Loading...';
      const last10Blocks = blocks.slice(0, Math.min(10, blocks.length));
      const totalFees = last10Blocks.reduce((sum, block) => sum + (block.extras?.totalFees || 0), 0);
      return `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(
        (totalFees / 100000000) * convertBitcoinPrice(bitcoinPrice),
        0
      )}`;
    })(),
    avgTxCount10: (() => {
      if (!blocks || blocks.length === 0) return 'Loading...';
      const last10Blocks = blocks.slice(0, Math.min(10, blocks.length));
      const avgTx = last10Blocks.reduce((sum, block) => sum + (block.tx_count || 0), 0) / last10Blocks.length;
      return `${formatNumber(avgTx, 0)} TX`;
    })(),
    emptyBlocks10: (() => {
      if (!blocks || blocks.length === 0) return 'Loading...';
      const last10Blocks = blocks.slice(0, Math.min(10, blocks.length));
      const emptyCount = last10Blocks.filter(block => block.tx_count === 1).length;
      return `${emptyCount}/10`;
    })(),
    largestBlock10: (() => {
      if (!blocks || blocks.length === 0) return 'Loading...';
      const last10Blocks = blocks.slice(0, Math.min(10, blocks.length));
      const largestSize = Math.max(...last10Blocks.map(block => block.size || 0));
      return `${formatLargeNumber(largestSize, 2, true)}B`;
    })(),
    customBag: (() => {
      if (!bitcoinPrice || !selectedCurrency || !currencySymbols[selectedCurrency]) return 'Loading...';
      
      // Remove commas for parsing
      const cleanAmount = vers.replace(/,/g, '');
      const amount = parseFloat(cleanAmount);
      if (isNaN(amount) || amount <= 0) return 'Enter amount';
      
      // Convert to BTC if in sats mode
      const btcAmount = versType === 'sats' ? amount / 100000000 : amount;
      const convertedValue = btcAmount * convertBitcoinPrice(bitcoinPrice);
      
      // Format according to currency
      const currencySymbol = currencySymbols[selectedCurrency];
      if (convertedValue >= 10000000) { // 10M or more
        return `${currencySymbol}${formatLargeNumber(convertedValue, 1, false)}`;
      } else {
        return `${currencySymbol}${formatNumber(convertedValue, 0)}`;
      }
    })(),
    mempoolValue: (() => {
      if (!mempoolInfo?.total_fee || !bitcoinPrice) return 'Loading...';
      const value = mempoolInfo.total_fee * convertBitcoinPrice(bitcoinPrice);
      return `${currencySymbols[selectedCurrency]}${formatNumber(value, 0)}`;
    })(),
    avgUtxoPerBlock: (() => {
      if (!blocks || blocks.length === 0) return 'Loading...';
      const last10Blocks = blocks.slice(0, Math.min(10, blocks.length));
      const totalUtxoChange = last10Blocks.reduce((sum, block) => {
        return sum + (block.extras?.utxoSetChange || 0);
      }, 0);
      const avgChange = totalUtxoChange / last10Blocks.length;
      return avgChange >= 0 ? `+${formatNumber(avgChange, 0)}` : formatNumber(avgChange, 0);
    })(),
    previousRetarget: da?.previousRetarget != null
      ? `${da.previousRetarget > 0 ? '+' : ''}${formatNumberClean(da.previousRetarget, 2)}%`
      : 'Loading...',
    medianBlockFee: lastMempoolBlockData?.extras?.medianFee != null
      ? `${lastMempoolBlockData.extras.medianFee < 1 ? formatNumber(lastMempoolBlockData.extras.medianFee, 2) : formatNumber(lastMempoolBlockData.extras.medianFee, 0)} ṩ/vB`
      : 'Loading...',
    estimatedRetargetDate: da?.estimatedRetargetDate != null
      ? (() => {
          const date = new Date(da.estimatedRetargetDate);
          const options = { month: 'short', day: 'numeric' };
          return date.toLocaleDateString('en-US', options);
        })()
      : 'Loading...',
  };

  if (!data || !bitcoinPrice || !lastMempoolBlockData || !fees || !mempoolInfo || !conversionRates || !selectedCurrency) {
    if (data === null || data === undefined) {
      // console.error("Error: Data is null or undefined");
    }
    if (bitcoinPrice === null || bitcoinPrice === undefined) {
      // console.error("Error: Bitcoin price is null or undefined");
    }
    if (lastMempoolBlockData === null || lastMempoolBlockData === undefined) {
      // console.error("Error: Last mempool block data is null or undefined");
    }
    if (fees === null || fees === undefined) {
      // console.error("Error: Fees data is null or undefined");
    }
    if (mempoolInfo === null || mempoolInfo === undefined) {
      // console.error("Error: Mempool info data is null or undefined");
    }
    if (conversionRates === null || conversionRates === undefined) {
      // console.error("Error: Conversion rates data is null or undefined");
    }
    if (selectedCurrency === null || selectedCurrency === undefined) {
      // console.error("Error: Selected currency data is null or undefined");
    }
    return <CustomSpinner />;
  } else {

    return (
      <>
        <Flex
          align="center"
          flexDirection={{ base: 'column', sm: 'column', 'sm2': 'row', md: 'row' }}
          flexWrap="wrap"
          mt={{ base: '60px', md: "30px" }}
          mb="20px"
          mx={{ base: '0px', md: "20px" }}
          justifyContent={{ base: 'center', sm: 'center', 'sm2': 'center', md: 'space-between' }}
        >
          <Flex align="center"
            columnGap={{ base: bitcoinPriceDisplayMode === 'blockHeight' ? 4 : 0, sm: 5, md: 8 }}
            justifyContent={{ base: bitcoinPriceDisplayMode === 'blockHeight' ? 'center' : 'space-around', sm: 'center', md: 'space-between' }}
            mr={{ base: 0, md: 10 }}
            mb={{ base: '30px', md: 0 }}
            px={{ base: 2, sm: 0 }}
            width={{ base: '100%', sm: 'auto' }}
          >
            <ThemedLogoIcon icon={faBtc} fontSize="94px" rotate={12} />
            <BitcoinPrice 
              bitcoinPrice={bitcoinPrice} 
              selectedCurrency={selectedCurrency} 
              blockHeight={lastMempoolBlockData?.height}
              displayMode={bitcoinPriceDisplayMode}
              onToggle={() => {
                const currentMode = bitcoinPriceDisplayMode;
                const newMode = currentMode === 'price' ? 'blockHeight' : 'price';
                
                // Determine which metric we're switching TO
                const metricWeWantToShow = newMode === 'price' ? 'bitcoinPrice' : 'blockHeight';
                
                // Check if the metric we want to show is already in Mix & Match
                const conflictPosition = selectedData.findIndex(id => dataOptions[id].id === metricWeWantToShow);
                
                if (conflictPosition !== -1) {
                  // The metric we're switching TO is already in Mix & Match, so swap them
                  setSelectedData((prevData) => {
                    const newData = [...prevData];
                    // Find what we're switching FROM
                    const metricWeAreLeaving = currentMode === 'price' ? 'bitcoinPrice' : 'blockHeight';
                    const replacementIndex = dataOptions.findIndex(option => option.id === metricWeAreLeaving);
                    // Put the old metric where the new one was
                    newData[conflictPosition] = replacementIndex;
                    return newData;
                  });
                }
                
                // Update the display mode
                setBitcoinPriceDisplayMode(newMode);
              }}
              data={data} 
            />

          </Flex>
          {/* Start Mix and Match Metrics */}
          <Grid
            templateColumns={{
              base: '1fr',
              xs: 'repeat(3, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(3, 1fr)',
              xl: 'repeat(6, 1fr)',
              '2xl': 'repeat(7, 1fr)'
            }}
            templateRows={{
              base: 'repeat(6, 1fr)',
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(2, 1fr)',
              xl: '1fr',
              '2xl': '1fr'
            }}
            columnGap={{ base: 8, xs: 8, 'sm2': 3, sm: 3, md: 3, lg: 3, xl: 4, '2xl': 5 }}
            rowGap={{ base: 8, xs: 8, 'sm2': 4, sm: 4, md: 4, lg: 4, xl: 5, '2xl': 5 }}
            flex="1"
            justifyItems="stretch"
            alignItems="center"
            maxW={'100%'}
            overflow="visible"
            px={{ base: 2, xs: 2, sm: 3, md: 0 }}
          >
            {selectedData.map((id, index) => {
              const hoverColor = colorMode === 'dark' ? 'white' : 'black';
              // Add randomness to animation timing
              const delay = index * 0.05; // Stagger start times
              const duration = 1.3 + (Math.random() * 0.4); // Random duration between 1.3-1.7s
              const animationString = animateMetrics ? `${wiggle} ${duration}s ease-in-out ${delay}s` : undefined;

              if (index === selectedData.length - 1 && (breakpoint === 'base' || breakpoint === 'xs' || breakpoint === 'sm2' || breakpoint === 'sm' || breakpoint === 'md' || breakpoint === 'lg' || breakpoint === 'xl')) {
                return (
                  <SingleDataValue
                    key={index}
                    id={dataOptions[id].id}
                    label={dataOptions[id].label}
                    value={valueMap[dataOptions[id].id]}
                    onClick={(id) => onOpen(id, index)}
                    display={{
                      base: 'none',
                      xs: 'none',
                      'sm2': 'none',
                      sm: 'none',
                      md: 'none',
                      lg: 'none',
                      xl: 'none',
                      '2xl': 'flex'
                    }}
                    hoverColor={hoverColor}
                    animation={animationString}
                  />
                );
              }
              return (
                <SingleDataValue
                  key={index}
                  id={dataOptions[id].id}
                  label={dataOptions[id].label}
                  value={valueMap[dataOptions[id].id]}
                  onClick={(id) => onOpen(id, index)}
                  hoverColor={hoverColor}
                  animation={animationString}
                />
              );
            })}
          </Grid>
          {/* End Mix and Match Metrics */}
        </Flex>


        {/* Start Mix and Match Metrics Modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay backgroundColor="rgba(16, 22, 26, 0.9)" />
          <ModalContent
            bg={modalBgColor}
            color={modalTextColor}
            borderWidth="1px"
            borderColor={modalBorderColor}
            maxW={{ base: '90%', sm: '85%', md: '5xl' }}
            borderRadius="6px"
            px={{ base: "8px", sm: "12px", md: "20px" }}
            pt={{ base: "8px", md: "10px" }}
            pb={{ base: "20px", md: "30px" }}
          >
            <ModalHeader px={{ base: 2, sm: 3, md: 6 }}>Mix & Match Metrics</ModalHeader>
            <ModalCloseButton
              zIndex={1}
              _hover={{ bg: 'gray.500' }}
              _focus={{ boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.6)' }}
            />
            <ModalBody px={{ base: 2, sm: 3, md: 6 }}>
              {modalStep === 'configure' ? (
                <VStack spacing={4}>
                  <Flex 
                    w="100%" 
                    direction="column" 
                    gap={3}
                    p={4}
                    borderRadius="8px"
                    bg={colorMode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.04)'}
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.2)'}
                  >
                    <Flex align="center" justify="space-between" mb={2}>
                      <Text fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wider" color={colorMode === 'dark' ? 'gray.400' : 'gray.700'}>
                        Stack Track Amount
                      </Text>
                      <ButtonGroup size="xs" isAttached>
                        <Button 
                          variant={versType === 'btc' ? 'solid' : 'ghost'}
                          colorScheme={versType === 'btc' ? 'orange' : 'gray'}
                          onClick={() => {
                            if (versType === 'sats' && vers) {
                              // Convert from sats to BTC (already stored without commas)
                              let btcAmount = parseFloat(vers) / 100000000;
                              // Cap at 21M BTC
                              if (btcAmount > 21000000) {
                                btcAmount = 21000000;
                              }
                              setVers(btcAmount.toFixed(8));
                            }
                            setVersType('btc');
                          }}
                          fontWeight="600"
                          px={3}
                        >
                          BTC
                        </Button>
                        <Button 
                          variant={versType === 'sats' ? 'solid' : 'ghost'}
                          colorScheme={versType === 'sats' ? 'orange' : 'gray'}
                          onClick={() => {
                            if (versType === 'btc' && vers) {
                              // Convert from BTC to sats
                              let satsAmount = parseFloat(vers) * 100000000;
                              // Cap at 2.1 quadrillion sats
                              if (satsAmount > 2100000000000000) {
                                satsAmount = 2100000000000000;
                              }
                              // Store as string without commas for the number input
                              setVers(Math.round(satsAmount).toString());
                            }
                            setVersType('sats');
                          }}
                          fontWeight="600"
                          px={3}
                        >
                          SATS
                        </Button>
                      </ButtonGroup>
                    </Flex>

                    <Flex gap={2} align="stretch" direction={{ base: 'column', md: 'row' }}>
                      <Flex gap={2} align="stretch" flex="1">
                        <InputGroup size="md" flex="1">
                          <InputLeftAddon 
                            bg={colorMode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'gray.100'}
                            borderColor={colorMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'gray.300'}
                            color={colorMode === 'dark' ? 'orange.400' : 'orange.600'}
                            fontWeight="bold"
                            fontSize="lg"
                          >
                            {versType === 'btc' ? '₿' : 'ṩ'}
                          </InputLeftAddon>
                          <Input
                            type={versType === 'btc' ? 'number' : 'text'}
                            value={versType === 'sats' && vers && !isNaN(Number(vers)) ? Number(vers).toLocaleString() : vers}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (versType === 'btc') {
                                // Limit to 8 decimal places and max 21M BTC
                                const numValue = parseFloat(value);
                                if (!isNaN(numValue) && numValue > 21000000) {
                                  setVers('21000000');
                                  return;
                                }
                                const parts = value.split('.');
                                if (parts[1] && parts[1].length > 8) {
                                  setVers(parts[0] + '.' + parts[1].slice(0, 8));
                                } else {
                                  setVers(value);
                                }
                              } else {
                                // For sats, remove commas and store as plain number string
                                const cleanValue = value.replace(/,/g, '');
                                if (/^\d*$/.test(cleanValue)) {
                                  const numValue = parseFloat(cleanValue);
                                  if (!isNaN(numValue) && numValue > 2100000000000000) {
                                    setVers('2100000000000000');
                                  } else {
                                    setVers(cleanValue);
                                  }
                                }
                              }
                            }}
                            placeholder={versType === 'btc' ? '0.00000000' : '0'}
                            step={versType === 'btc' ? '0.00000001' : '1'}
                            min="0"
                            bg={colorMode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'white'}
                            borderColor={colorMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'gray.300'}
                            _hover={{ borderColor: colorMode === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'gray.400' }}
                            _focus={{ borderColor: 'orange.400', boxShadow: '0 0 0 1px rgba(251, 146, 60, 0.4)' }}
                            fontWeight="500"
                          />
                        </InputGroup>

                        <Flex 
                          align="center" 
                          px={3}
                          borderRadius="6px"
                          bg={colorMode === 'dark' ? 'rgba(251, 146, 60, 0.08)' : 'rgba(251, 146, 60, 0.12)'}
                          border="1px solid"
                          borderColor={colorMode === 'dark' ? 'rgba(251, 146, 60, 0.2)' : 'rgba(251, 146, 60, 0.3)'}
                          minW="140px"
                          display={{ base: 'none', sm: 'flex' }}
                        >
                          <VStack spacing={0} align="flex-start" w="100%">
                            <Text fontSize="9px" fontWeight="500" textTransform="uppercase" letterSpacing="wider" color={colorMode === 'dark' ? 'orange.400' : 'orange.600'} lineHeight="1">
                              Value
                            </Text>
                            <Text fontSize="sm" fontWeight="700" color={colorMode === 'dark' ? 'orange.300' : 'orange.600'} lineHeight="1.2">
                              {valueMap.customBag}
                            </Text>
                          </VStack>
                        </Flex>
                      </Flex>

                      <Button
                        colorScheme="orange"
                        size="md"
                        onClick={() => {
                          // Apply the Stack Track swap
                          setSelectedData((prevData) => {
                            const newData = [...prevData];
                            const customBagIndex = dataOptions.findIndex((option) => option.id === 'customBag');
                            
                            if (selectedPosition !== null && customBagIndex !== -1) {
                              // Check if custom bag is already displayed
                              const existingPosition = newData.findIndex((id) => id === customBagIndex);
                              
                              if (existingPosition !== -1) {
                                // Swap the two positions
                                [newData[selectedPosition], newData[existingPosition]] = [newData[existingPosition], newData[selectedPosition]];
                              } else {
                                // Replace the metric at the selected position
                                newData[selectedPosition] = customBagIndex;
                              }
                            }
                            
                            return newData;
                          });
                          onClose();
                        }}
                        isDisabled={!vers || parseFloat(vers.replace(/,/g, '')) <= 0}
                        fontWeight="600"
                        px={6}
                        w={{ base: '100%', md: 'auto' }}
                      >
                        Apply
                      </Button>
                    </Flex>

                    <Flex 
                      align="center" 
                      justify="center"
                      p={3}
                      borderRadius="6px"
                      bg={colorMode === 'dark' ? 'rgba(251, 146, 60, 0.08)' : 'rgba(251, 146, 60, 0.12)'}
                      border="1px solid"
                      borderColor={colorMode === 'dark' ? 'rgba(251, 146, 60, 0.2)' : 'rgba(251, 146, 60, 0.3)'}
                      display={{ base: 'flex', sm: 'none' }}
                      mt={2}
                    >
                      <Text fontSize="xs" fontWeight="600" color={colorMode === 'dark' ? 'orange.300' : 'orange.600'}>
                        Value: {valueMap.customBag}
                      </Text>
                    </Flex>
                  </Flex>

                  <Flex
                    align="center"
                    justify="center"
                    gap={3}
                    p={0}
                    // borderRadius="8px"
                    // bg={colorMode === 'dark' ? 'rgba(251, 146, 60, 0.05)' : 'rgba(251, 146, 60, 0.08)'}
                    // border="1px solid"
                    // borderColor={colorMode === 'dark' ? 'rgba(251, 146, 60, 0.15)' : 'rgba(251, 146, 60, 0.25)'}
                  >
                    <Text fontSize="lg">🔒</Text>
                    <Text 
                      fontSize="sm" 
                      color={colorMode === 'dark' ? 'gray.200' : 'gray.700'}
                      lineHeight="1.4"
                    >
                      <Text as="span" fontWeight="700" color={colorMode === 'dark' ? 'orange.300' : 'orange.700'} mr={1}>
                        Privacy
                      </Text>
                      {' '}Stack amounts are temporarily stored in your browser session ONLY. Timechain Stats cannot see this data.
                    </Text>
                  </Flex>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setModalStep('select')}
                    color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}
                  >
                    ← Back to Mix & Match Metrics
                  </Button>
                </VStack>
              ) : (
                <>
                  <Grid
                    templateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)', xl: 'repeat(6, 1fr)', '2xl': 'repeat(6, 1fr)' }}
                    columnGap={{ base: 2, sm: 2, md: 3 }}
                    rowGap={{ base: 3, sm: 3, md: 4 }}
                  >
                    {dataOptions.map((option) => {
                      const isSelected = option.id === currentlySelectedId;
                      return (
                        <Button
                          key={option.id}
                          variant={isSelected ? 'solid' : 'outline'}
                          colorScheme="orange"
                          onClick={() => {
                            // Always open Stack Track config modal
                            if (option.id === 'customBag') {
                              setModalStep('configure');
                            } else {
                              handleSwap(option.id);
                            }
                          }}
                          size="sm"
                          whiteSpace="normal"
                          height="auto"
                          py={2}
                          isDisabled={isSelected && option.id !== 'customBag'}
                        >
                          {option.label}
                        </Button>
                      );
                    })}
                  </Grid>
                  <Text textAlign="center" mt={{ base: 6, md: 10 }} fontSize={{ base: "lg", md: "xl" }} color={itsUpToYou}>
                    It's Up To You
                  </Text>
                </>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
        {/* End Mix and Match Metrics Modal */}

        <MetricsTutorial
          isOpen={showTutorial}
          onClose={handleTutorialClose}
        />
      </>
    );
  };
}
